import Vue from 'vue'
import base from "@/utils/base";
import store from "@/store/index";
import router from "@/router";
import { getToken } from "@/utils/auth";

let socket = null;
let lockReconnet = false; //避免重复连接
const wsUrl = base.get().ws;
let isReconnet = false;
let globalCallback = null,
  sendData = null; //把要发送给socket的数据和处理socket返回数据的回调保存起来

// ==================== 重连机制 ====================
let reconnectCount = 0;          // 当前已重连次数
const MAX_RECONNECT = 10;         // 最大重连次数
const BASE_DELAY = 2000;          // 基础重连间隔（ms）
const MAX_DELAY = 30000;          // 最大重连间隔（ms）
let reconnectTimer = null;        // 重连定时器
let manuallyClosed = false;       // 用户主动关闭标记，主动关闭不触发重连
let hasNotifiedDisconnect = false; // 是否已弹出断开通知（避免重复弹）

/**
 * 计算指数退避延迟：BASE_DELAY * 2^attempt，上限 MAX_DELAY
 */
function calcReconnectDelay() {
  const delay = Math.min(BASE_DELAY * Math.pow(2, reconnectCount), MAX_DELAY);
  // 加 ±500ms 随机抖动，避免多实例同时重连
  const jitter = Math.floor(Math.random() * 1000) - 500;
  return delay + jitter;
}

/**
 * 更新 Vuex 中的 WebSocket 连接状态
 */
function updateWsStatus(status) {
  store.commit('setWsStatus', status);
}

/**
 * 重连调度：指数退避 + 最大次数限制
 */
function scheduleReconnect(url) {
  if (manuallyClosed) return;            // 主动关闭不重连
  if (lockReconnet) return;              // 防止并发重连
  if (reconnectCount >= MAX_RECONNECT) {
    console.log('[WS] 已达最大重连次数(' + MAX_RECONNECT + ')，停止重连');
    updateWsStatus('disconnected');
    if (!hasNotifiedDisconnect) {
      hasNotifiedDisconnect = true;
      try {
        Vue.prototype.$notify({
          title: '实时监听已断开',
          message: '多次重连失败，请检查网络后刷新页面',
          type: 'error',
          duration: 0,
          position: 'top-right'
        });
      } catch (e) {}
    }
    return;
  }

  lockReconnet = true;
  isReconnet = true;
  reconnectCount++;

  const delay = calcReconnectDelay();
  console.log(`[WS] 将在 ${delay}ms 后进行第 ${reconnectCount}/${MAX_RECONNECT} 次重连`);
  updateWsStatus('reconnecting');

  reconnectTimer = setTimeout(() => {
    lockReconnet = false;
    createSocket(url);
  }, delay);
}

/**
 * 重连成功后重置计数器并通知 UI
 */
function onReconnectSuccess() {
  reconnectCount = 0;
  hasNotifiedDisconnect = false;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  updateWsStatus('connected');
  // 重连成功后提示用户
  if (isReconnet) {
    try {
      Vue.prototype.$notify({
        title: '实时监听已恢复',
        message: 'WebSocket 重连成功',
        type: 'success',
        duration: 3000,
        position: 'top-right'
      });
    } catch (e) {}
  }
}

/**
 * 主动关闭 WebSocket 连接（不触发重连）
 */
function closeSocket() {
  manuallyClosed = true;
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (socket) {
    try { socket.close(); } catch (e) {}
  }
  updateWsStatus('disconnected');
}

// ==================== Socket 生命周期 ====================

let createSocket = (url) => {
  // 每次主动发起连接时，重置主动关闭标记，确保断线后可以自动重连
  manuallyClosed = false;
  //创建socket
  try {
    if ("WebSocket" in window) {
      socket = new WebSocket(url);
    } else if ("MozWebSocket" in window) {
      socket = new MozWebSocket(url);
    }
    Vue.prototype.socket = socket
    initSocket();
  } catch (e) {
    scheduleReconnect(url);
  }
};

let sendMsg = (data, callback) => {
  //发送数据,接收数据
  if (socket && socket.readyState === 1) {
    globalCallback = callback;
    sendData = data;

    data = JSON.stringify(data);
    socket.send(data);
  } else {
    setTimeout(() => {
      console.log(socket, "等待socket链接成功");
      sendMsg(data, callback);
    }, 1500);
    return false;
  }
  socket.onmessage = (ev) => {
    callback && callback(ev);
  };
};

let initSocket = () => {
  //初始化websocket
  socket.onopen = () => {
    console.log("[WS] 连接成功");
    updateWsStatus('connected');

    if (isReconnet) {
      //重连成功，重置状态并通知
      onReconnectSuccess();
      isReconnet = false;
    }
  };

  socket.onmessage = (ev) => {
    handleOrderMessage(ev.data);
  };

  socket.onerror = () => {
    console.log("[WS] 连接错误");
    // onerror 通常紧随 onclose 触发，由 onclose 统一处理重连
  };

  socket.onclose = (event) => {
    console.log("[WS] 连接关闭，code=" + event.code + "，reason=" + event.reason);
    updateWsStatus('disconnected');
    // 非主动关闭时，弹出断开提示并启动自动重连
    if (!manuallyClosed) {
      if (!hasNotifiedDisconnect) {
        hasNotifiedDisconnect = true;
        try {
          Vue.prototype.$notify({
            title: '实时监听已断开',
            message: '正在尝试重新连接...',
            type: 'warning',
            duration: 5000,
            position: 'top-right'
          });
        } catch (e) {}
      }
      scheduleReconnect(wsUrl);
    }
  };
};

// 事件类型描述
const EVENT_META = {
  1: { title: "新订单提醒", icon: "🛍️", type: "success", badge: true, color: "#67c23a", duration: 10000 },
  2: { title: "订单已取消", icon: "⚠️", type: "info", badge: false, color: "#909399", duration: 5000 },
  3: { title: "退款申请待处理", icon: "🔴", type: "warning", badge: true, color: "#e6a23c", duration: 10000 },
  4: { title: "订单已完成", icon: "✅", type: "success", badge: false, color: "#409eff", duration: 5000 },
  5: { title: "新订单待付款", icon: "🛒", type: "warning", badge: true, color: "#e6a23c", duration: 10000 }
};

// HTML 转义
const escapeHtml = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// 时间格式化 yyyy-MM-dd HH:mm:ss
const formatTime = (ts) => {
  const d = ts ? new Date(ts) : new Date();
  const p = (n) => (n < 10 ? "0" + n : "" + n);
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
};

// 弹出 Element 右上角通知（富文本、可点击跳转）
let popNotify = (meta, data) => {
  if (!Vue.prototype.$notify) return;
  try {
    const orderNumHtml = data.orderNum
      ? `<div class="on-num">订单号：<span>${escapeHtml(data.orderNum)}</span></div>`
      : "";
    const msg = `
      <div class="order-notify-body">
        <div class="on-content">${escapeHtml(data.content || "请及时处理")}</div>
        ${orderNumHtml}
        <div class="on-foot">
          <span class="on-time">${formatTime(data.time)}</span>
          <span class="on-link">点击查看 →</span>
        </div>
      </div>`;
    Vue.prototype.$notify({
      title: meta.icon + " " + meta.title,
      dangerouslyUseHTMLString: true,
      message: msg,
      type: meta.type,
      position: "top-right",
      duration: meta.duration,
      showClose: true,
      customClass: "order-notify-wrap order-notify-t" + data.type,
      onClick: () => {
        try {
          if (router.currentRoute.path !== "/orderAdm") {
            sessionStorage.setItem("n", "4");
            router.push("/orderAdm").catch(() => {});
          }
        } catch (e) {}
      }
    });
  } catch (e) {}
};

// 请求浏览器桃面通知权限
let ensureDesktopPermission = () => {
  if (typeof Notification === "undefined") return;
  if (Notification.permission === "default") {
    try { Notification.requestPermission(); } catch (e) {}
  }
};

// 浏览器桌面通知（页面未激活时依然可见）
let pushDesktop = (meta, data) => {
  try {
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "granted") return;
    if (document.visibilityState === "visible") return; // 页面在前台时暂不重复弹
    const n = new Notification(meta.icon + " " + meta.title, {
      body: (data.content || "") + (data.orderNum ? "\n订单号：" + data.orderNum : ""),
      tag: data.orderId || String(Date.now())
    });
    n.onclick = () => {
      try {
        window.focus();
        if (router.currentRoute.path !== "/orderAdm") {
          sessionStorage.setItem("n", "4");
          router.push("/orderAdm").catch(() => {});
        }
      } catch (e) {}
      n.close && n.close();
    };
    setTimeout(() => n.close && n.close(), 8000);
  } catch (e) {}
};

let handleOrderMessage = (raw) => {
  let data = null;
  try { data = typeof raw === "string" ? JSON.parse(raw) : raw; } catch (e) {}
  if (!data || typeof data !== "object") return;
  // 过滤面向用户的消息（type >= 10），管理端只处理 type < 10 的消息
  if (data.type && data.type >= 10) return;
  const meta = EVENT_META[data.type] || { title: "订单状态更新", icon: "🔔", type: "", badge: false, color: "#909399", duration: 6000 };
  // 待处理事件才累加待办徽章
  if (meta.badge) {
    const cur = Number(localStorage.getItem("order_num") || 0) + 1;
    localStorage.setItem("order_num", cur);
    store.commit("order_remind", cur);
  }
  const enriched = Object.assign({}, data, {
    key: Date.now() + "-" + Math.random(),
    title: meta.title,
    icon: meta.icon,
    color: meta.color
  });
  // 记入全局通知中心（铃铛面板使用）
  store.commit("pushNotify", enriched);
  // 弹出右上角通知
  popNotify(meta, data);
  // 后台页面推送桌面通知
  pushDesktop(meta, data);
  // 分发事件给当前页面（订单管理页/首页）自动刷新
  store.commit("setOrderEvent", enriched);
};

// 保留旧函数名兼容，内部转发到新的重连机制
let reconnet = (url) => {
  scheduleReconnect(url);
};

let heartCheck = {
  //心跳检测
  timeout: 60 * 1000,
  timeoutObj: null,
  serverTimeoutObj: null,
  reset() {
    clearTimeout(this.timeoutObj);
    clearTimeout(this.serverTimeoutObj);
    return this;
  },
  start() {
    let that = this;
    this.timeoutObj = setTimeout(() => {
      if (socket && socket.readyState === 1) {
        socket.send("heart check");
      }
      this.serverTimeoutObj = setTimeout(() => {
        if (socket) socket.close();
      }, that.timeout);
    }, this.timeout);
  },
};

if (getToken()) {
  createSocket(wsUrl);
  ensureDesktopPermission();
}

export default { sendMsg, createSocket, ensureDesktopPermission, closeSocket, updateWsStatus };
